import { useState } from "react";
import { Plus } from "lucide-react";
import { PRODUCT_COL_WIDTH } from "@/lib/gridLayout";
import { Button } from "@/components/ui/Button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { groupCustomersForPicker, type CustomerInfo } from "../lib/forecastGrid";

interface Props {
  productName: string;
  monthsCount: number;
  availableCustomers: CustomerInfo[];
  currentUserId: string | null;
  onAdd: (customerId: string) => void;
}

// Ghost row at the bottom of a product group: click to reveal a customer
// picker, grouped "Your customers" first so PICs find their own accounts
// without scrolling past the whole org roster. Lives in the sticky first
// column so it stays visible while the months scroll.
export default function AddCustomerRow({
  productName,
  monthsCount,
  availableCustomers,
  currentUserId,
  onAdd,
}: Props) {
  const [picking, setPicking] = useState(false);

  if (availableCustomers.length === 0) return null;

  const { mine, others } = groupCustomersForPicker(availableCustomers, currentUserId);

  return (
    <tr style={{ borderColor: "var(--accent)" }} className="border-b">
      <td
        style={{ background: "var(--background)", borderColor: "var(--border)", width: PRODUCT_COL_WIDTH, maxWidth: PRODUCT_COL_WIDTH }}
        className="sticky left-0 z-10 border-r px-3 py-1"
      >
        {picking ? (
          <Select
            defaultOpen
            onValueChange={(customerId) => {
              onAdd(customerId);
              setPicking(false);
            }}
            onOpenChange={(next) => {
              if (!next) setPicking(false);
            }}
          >
            <SelectTrigger className="h-7 w-full text-xs">
              <SelectValue placeholder="Select a customer…" />
            </SelectTrigger>
            <SelectContent>
              {mine.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Your customers</SelectLabel>
                  {mine.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {others.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Others</SelectLabel>
                  {others.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPicking(true)}
            title={`Add customer to ${productName}`}
            className="ml-4 h-7 gap-1 px-2 font-normal text-muted-foreground hover:text-foreground"
          >
            <Plus size={12} />
            Add customer
          </Button>
        )}
      </td>
      <td colSpan={monthsCount} style={{ background: "var(--background)" }} />
    </tr>
  );
}
